import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth';
import {
  getTBM,
  distributeTBM,
  completeTBM,
  addEducationText,
  uploadEducationPhoto,
  deleteEducationEntry,
  updateEducationText,
  reorderEducationEntries,
  getEducationAnnotation,
  saveEducationAnnotation,
  updateTbmSessionRecords,
  TBM,
} from '../../../lib/tbm';
import { extractApiError } from '../../../lib/api';
import { ChevronLeftIcon } from '../../../components/icons';
import { LocaleProvider } from '../../../lib/i18n/useLocale';
import { useWebSocketStore } from '../../../stores/websocket';
import { ServerEvents, TbmEventPayload, ApprovalEventPayload } from '@safetyhub/shared';
import { TbmEducateView } from '../../../components/tbm/TbmEducateView';
import type { TbmEducateAdapter } from '../../../components/tbm/tbmEducateAdapter';

/**
 * 안전관리자 직접 교육 화면 (/educations/tbm/[id], 인증 진입).
 *
 * 교육담당자 토큰 화면(/educations/tbm/educator/[token])과 동일한 교육 진행 UI(TbmEducateView)를
 * 인증 기반 어댑터로 재사용한다. 교육 시작·QR·직접서명·교육기록 편집·교육 완료(세션)를
 * 여기서 진행하고, 헤더 "← 상세"로 관리 상세(/tbm/[id])로 복귀한다.
 */
export default function TbmEducatePage() {
  return (
    <LocaleProvider>
      <TbmEducatePageInner />
    </LocaleProvider>
  );
}

function TbmEducatePageInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const educatorName = user?.name ?? '안전관리자';
  const { subscribe, isConnected, isAuthenticated } = useWebSocketStore();

  const [tbm, setTbm] = useState<TBM | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!id || !companyId) return;
    try {
      setIsLoading(true);
      setTbm(await getTBM(id, companyId));
      setError('');
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [id, companyId]);

  // 스크롤 유지한 채 조용히 갱신 (교육 기록 편집 후)
  const refreshData = useCallback(async () => {
    if (!id || !companyId) return;
    try {
      setTbm(await getTBM(id, companyId));
    } catch {
      // 조용한 갱신 실패는 무시 (다음 액션에서 재시도)
    }
  }, [id, companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 실시간 이벤트 구독 (인증 진입 전용 — 토큰 educator 페이지는 무인증이라 별도).
  // 다른 탭/담당자의 회차 서명·완료·결재 처리를 새로고침 없이 반영한다.
  //
  // ⚠️ **전부 조용한 `refreshData`다** (F-40, 2026-09-12). 종전에는 완료·결재 승인/반려만
  // `loadData`를 불렀는데(「상태 전환이 크므로」), 그러면 `setIsLoading(true)` → 아래 스피너가
  // TbmEducateView를 **언마운트**했다가 다시 마운트한다. 사용자에게는 남이 결재할 때마다 교육
  // 화면이 통째로 깜빡이는 것으로 보이고, 진행 중이던 입력·스크롤도 그때 날아간다. 상태 전환은
  // TbmEducateView가 `tbm.status`·`tbm.qrSession`·`tbm.attendees` 동기화 effect로 받으므로
  // 조용한 갱신으로 충분하다. `loadData`는 초기 로드와 `onReload`(사용자 본인 행위) 전용이다.
  //
  // ⚠️ `TBM_COMPLETED` 구독은 **걷어냈다**(2026-09-13). 서버가 그 이벤트를 어디서도 emit하지
  // 않아(`apps/server/src` 기준 0건) 한 번도 발화하지 않았고, 아래 `TBM_UPDATED` 구독이 같은
  // 핸들러·같은 게이트라 교육 완료도 이미 그리로 온다(`emitEducationChanged`). 죽은 구독을
  // 남겨 두면 다음 사람이 거기에 갱신을 걸고 **조용히 아무 일도 일어나지 않는다**.
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !id || !companyId) return;

    const unsubSigned = subscribe<TbmEventPayload>(
      ServerEvents.TBM_ATTENDEE_SIGNED,
      (payload) => {
        if (payload.tbmId === id) refreshData();
      },
    );
    const unsubUpdated = subscribe<TbmEventPayload>(
      ServerEvents.TBM_UPDATED,
      (payload) => {
        if (payload.tbmId === id) refreshData();
      },
    );
    const unsubApprovalSigned = subscribe<ApprovalEventPayload>(
      ServerEvents.APPROVAL_SIGNED,
      (payload) => {
        if (payload.reportId === id) refreshData();
      },
    );
    const unsubApprovalRejected = subscribe<ApprovalEventPayload>(
      ServerEvents.APPROVAL_REJECTED,
      (payload) => {
        if (payload.reportId === id) refreshData();
      },
    );

    return () => {
      unsubSigned();
      unsubUpdated();
      unsubApprovalSigned();
      unsubApprovalRejected();
    };
  }, [isConnected, isAuthenticated, id, companyId, subscribe, refreshData]);

  // 인증 기반 어댑터 — 토큰 화면의 *ByToken 대신 인증 API(companyId 동반)를 호출
  const adapter: TbmEducateAdapter = useMemo(
    () => ({
      educatorName,
      refreshTbm: () => getTBM(id!, companyId!),
      startEducation: () => distributeTBM(id!, companyId!),
      complete: (dto) =>
        completeTBM(
          id!,
          companyId!,
          dto.signatureData ? { name: educatorName, signatureData: dto.signatureData } : undefined,
          dto.safetyCheckNotes,
          dto.safetyChecks,
          dto.specialNotes,
          // 대상 회차(F-21) — 여기서 안 넘기면 서버 가드가 «말하지 않은 것»으로 보고 종전 동작으로 간다.
          dto.sessionId,
        ),
      updateSessionRecords: (sessionId, records) =>
        updateTbmSessionRecords(id!, sessionId, companyId!, records),
      addText: (content) => addEducationText(id!, content, companyId!),
      uploadPhoto: (file) => uploadEducationPhoto(id!, file, companyId!),
      deleteEntry: (entryId) => deleteEducationEntry(id!, entryId, companyId!),
      updateText: (entryId, content) => updateEducationText(id!, entryId, content, companyId!),
      reorder: (entries) => reorderEducationEntries(id!, entries, companyId!),
      getAnnotation: (entryId) => getEducationAnnotation(id!, entryId, companyId!),
      saveAnnotation: (entryId, data) => saveEducationAnnotation(id!, entryId, companyId!, data),
      annotationCacheKey: `educationAnnotation-${id}`,
    }),
    [id, companyId, educatorName],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
          <p className="mt-4 text-ink-600">교육 화면 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error || !tbm) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'TBM을 찾을 수 없습니다.'}</p>
          <Link
            to={`/reports/${id}`}
            className="mt-4 inline-flex items-center gap-1 text-brand-600 hover:text-brand-800"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            상세로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <TbmEducateView
      tbm={tbm}
      adapter={adapter}
      onBack={() => navigate(`/reports/${id}`)}
      onReload={loadData}
      onRefresh={refreshData}
    />
  );
}
